import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Clock,
  Star,
  Target,
  CheckCircle,
  XCircle,
  Trophy,
  Coins,
  BookOpen,
  Zap,
  Search,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/spotWordGame.css";
import { API_BASE } from "../config";
import gameConfig from "../data/gameConfig.json";
import { calcTimeBonusPct } from "../utils/scoring";
import apiFetch from "../apiFetch";

const MODE_CFG = gameConfig.spotWord;

// Strips punctuation and dashes for loose word matching
const normForMatch = (w) => w.toLowerCase().replace(/[.,!?;:"'()]/g, "").replace(/-/g, "");

// ── Completion effects ──────────────────────────────────────────────────────

const CONFETTI_COLORS = ["#f59e0b","#10b981","#6366f1","#ef4444","#ec4899","#14b8a6","#f97316","#8b5cf6"];

function launchConfetti(canvas) {
  if (!canvas) return () => {};
  const ctx = canvas.getContext("2d");
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  const particles = Array.from({ length: 180 }, () => ({
    x: Math.random() * canvas.width,
    y: -10 - Math.random() * 120,
    w: 8 + Math.random() * 10,
    h: 4 + Math.random() * 6,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    vx: -2.5 + Math.random() * 5,
    vy: 3 + Math.random() * 5,
    rot: Math.random() * 360,
    rotV: (-1 + Math.random() * 2) * 4,
    circle: Math.random() > 0.6,
  }));

  let raf;
  let tick = 0;

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const alpha = tick < 100 ? 1 : Math.max(0, 1 - (tick - 100) / 60);
    particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.rotV;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rot * Math.PI) / 180);
      ctx.fillStyle = p.color;
      if (p.circle) {
        ctx.beginPath();
        ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      }
      ctx.restore();
    });
    tick++;
    if (alpha > 0) raf = requestAnimationFrame(draw);
  }

  draw();
  return () => cancelAnimationFrame(raf);
}

function playVictoryFanfare() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ac = new AC();
    const master = ac.createGain();
    master.gain.value = 0.28;
    master.connect(ac.destination);

    // C5 E5 G5 C6 — ascending fanfare then hold
    const notes = [
      { f: 523.25, t: 0.0,  d: 0.18 },
      { f: 659.25, t: 0.16, d: 0.18 },
      { f: 783.99, t: 0.32, d: 0.18 },
      { f: 1046.5, t: 0.48, d: 0.55 },
      { f: 783.99, t: 0.52, d: 0.25 },
      { f: 1046.5, t: 0.80, d: 0.80 },
    ];

    notes.forEach(({ f, t, d }) => {
      const osc = ac.createOscillator();
      const env = ac.createGain();
      osc.connect(env);
      env.connect(master);
      osc.type = "sine";
      osc.frequency.value = f;
      const s = ac.currentTime + t;
      env.gain.setValueAtTime(0.001, s);
      env.gain.linearRampToValueAtTime(1, s + 0.03);
      env.gain.exponentialRampToValueAtTime(0.001, s + d);
      osc.start(s);
      osc.stop(s + d + 0.05);
    });
  } catch (e) {
    // Audio not supported — silently skip
  }
}

function sayWellDone() {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance("Well done! Excellent work!");
    utt.rate = 0.9;
    utt.pitch = 1.2;
    utt.volume = 0.85;

    const assignVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      // 1. Named British female voices (Chrome/Edge/macOS/Windows)
      const britishFemale =
        voices.find((v) => /google uk english female/i.test(v.name)) ||
        voices.find((v) => /en-GB/i.test(v.lang) && /female|serena|martha|kate|hazel|emily|alice/i.test(v.name)) ||
        voices.find((v) => /en-GB/i.test(v.lang));
      if (britishFemale) utt.voice = britishFemale;
      window.speechSynthesis.speak(utt);
    };

    const voices = window.speechSynthesis.getVoices();
    if (voices.length) {
      assignVoice();
    } else {
      window.speechSynthesis.onvoiceschanged = assignVoice;
    }
  } catch (e) {
    // Speech not supported — silently skip
  }
}

// ───────────────────────────────────────────────────────────────────────────

const SPOT_CACHE_KEY = (tier, topicCategory) => `spot_cache_tier_${tier}_topic_${topicCategory}`;

const getCachedGameData = (tier, topic) => {
  try {
    const raw = localStorage.getItem(SPOT_CACHE_KEY(tier, topic));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedGameData = (tier, topic, data) => {
  try {
    localStorage.setItem(SPOT_CACHE_KEY(tier, topic), JSON.stringify(data));
  } catch {}
};

const clearCachedGameData = (tier, topic) => {
  localStorage.removeItem(SPOT_CACHE_KEY(tier, topic));
};

// ====================================
// MAIN GAME COMPONENT
// ====================================
const SpotWordGame = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTier = location.state?.tier || "1";
  const selectedTierName = location.state?.tierName || "Novice";
  const selectedTopicCategory = location.state?.topicCategory || "Adventure";

  // ====================================
  // STATE MANAGEMENT
  // ====================================

  // Core game state
  const [gameState, setGameState] = useState("loading"); // 'loading', 'playing', 'finished'
  const [passage, setPassage] = useState("");
  const [wordsToFind, setWordsToFind] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [wordInfoMap, setWordInfoMap] = useState({}); // word → { meaning, synonyms, antonyms }

  // Scoring and timing
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(MODE_CFG.time);

  // Test mode
  const [testMode, setTestMode] = useState(false);
  const [showTestModeModal, setShowTestModeModal] = useState(false);

  // Game statistics tracking
  const [gameStats, setGameStats] = useState({
    totalWords: 0,
    correctFinds: 0,
    incorrectAttempts: 0,
    timeBonus: 0,
  });

  const [gameFilename, setGameFilename] = useState(null);
  const [gameLevel, setGameLevel] = useState(null);

  // UI feedback and loading states
  const [feedback, setFeedback] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ====================================
  // GAME CONFIGURATION
  // ====================================

  // Fallback data when API is unavailable
  const fallbackGameData = {
    passage:
      "The ancient library stood majestically against the twilight sky, its towering spires reaching toward the heavens like fingers grasping for knowledge itself. Within its hallowed halls, countless volumes contained the wisdom of ages past, their leather-bound covers weathered by time but their contents preserved for eternity. Scholars from distant lands would journey for months to access these precious manuscripts, believing that within these pages lay the secrets to understanding the very fabric of existence. The librarian, a keeper of this sacred knowledge, moved silently through the corridors, her footsteps echoing softly against the marble floors as she tended to her beloved collection.",
    targetWords: [
      "ancient",
      "majestically",
      "knowledge",
      "manuscripts",
      "librarian",
    ],
  };

  // ====================================
  // SCORING AND REWARDS SYSTEM
  // ====================================

  /**
   * Calculates score and coins based on game performance
   * @param {Object} stats - Current game statistics
   * @param {number} timeRemaining - Seconds left on timer
   * @returns {Object} - Contains finalScore and coinsEarned
   */
  const calculateScoreAndCoins = (stats, timeRemaining) => {
    const total = wordsToFind.length || 1;
    const accuracy = stats.correctFinds / total;
    const bonusPct = calcTimeBonusPct(accuracy, timeRemaining, MODE_CFG.time, MODE_CFG.maxTimeBonusPct);

    const wordScore = Math.max(0,
      stats.correctFinds * MODE_CFG.pointsPerCorrect -
      stats.incorrectAttempts * MODE_CFG.penaltyPerWrong
    );
    const timeBonus = Math.round(wordScore * bonusPct);
    const finalScore = wordScore + timeBonus;
    const coinsEarned = Math.floor(finalScore * MODE_CFG.coinsMultiplier);

    return {
      finalScore,
      coinsEarned,
      timeBonus,
      breakdown: {
        wordScore,
        timeBonus,
        totalDeductions: stats.incorrectAttempts * MODE_CFG.penaltyPerWrong,
      },
    };
  };

  // ====================================
  // GAME LIFECYCLE HOOKS
  // ====================================

  // Initialize game on component mount
  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    initializeGame();
  }, []);

  // Handle game timer countdown
  useEffect(() => {
    if (gameState === "playing" && timeLeft > 0 && !testMode) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === "playing" && !testMode) {
      endGame();
    }
  }, [timeLeft, gameState, testMode]);

  // ====================================
  // API AND DATA MANAGEMENT
  // ====================================

  /**
   * Fetches new game data from backend API
   * Falls back to local data if API fails
   */
  const fetchGameData = async () => {
    setLoading(true);
    setError("");

    try {
      const cached = getCachedGameData(selectedTier, selectedTopicCategory);
      if (cached && cached.level === parseInt(selectedTier, 10)) return cached;

      const userId = parseInt(localStorage.getItem("userId"), 10) || undefined;
      const response = await apiFetch(`/api/generate-passage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, tier: selectedTier, tierName: selectedTierName, topicCategory: selectedTopicCategory }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.passage || !data.targetWords || data.targetWords.length === 0) {
        throw new Error("Invalid data received from API");
      }

      setCachedGameData(selectedTier, selectedTopicCategory, data);
      return data;
    } catch (error) {
      console.error("Error fetching game data:", error);
      navigate("/error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Awards coins to the user account via API call
   * @param {number} coinsToAdd - Number of coins to award
   */
  const addCoinsAndXPToUser = async (coinsToAdd) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || coinsToAdd <= 0) return;

    try {
      // 1) Add coins
      const coinsRes = await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: coinsToAdd }),
      });

      if (!coinsRes.ok) {
        console.error("Failed to add coins:", await coinsRes.text());
        return;
      }

      // 2) Add XP (coins × 5)
      const xpDelta = coinsToAdd * MODE_CFG.xpMultiplier;
      const xpRes = await apiFetch(`/api/resources/xp/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, delta: xpDelta }),
      });

      if (!xpRes.ok) {
        console.error("Failed to add XP:", await xpRes.text());
        // (Optional) Consider showing a toast; coins have already been added.
        return;
      }
    } catch (error) {
      console.error("Error adding coins:", error);
    }
  };

  // ====================================
  // GAME CONTROL METHODS
  // ====================================

  /**
   * Initializes a new game session
   * Resets all state and fetches fresh content
   */
  const initializeGame = async () => {
    hasPostedRef.current = false; // reset duplicate-post guard for a new round
    setTestMode(false);
    setGameState("loading");

    // Fetch new game content
    const gameData = await fetchGameData();
    if (!gameData) return;

    // Reset all game state
    setGameFilename(gameData.filename || null);
    setGameLevel(gameData.level ?? null);
    setPassage(gameData.passage);

    // Filter out any target words that don't actually appear in the passage
    const passageNorms = new Set(gameData.passage.split(/\s+/).map(normForMatch));
    const validTargets = (gameData.targetWords || [])
      .map((w) => w.toLowerCase())
      .filter((w) => passageNorms.has(normForMatch(w)));
    setWordsToFind(validTargets);

    // Build word info lookup map
    const infoMap = {};
    (gameData.wordInfo || []).forEach((wi) => {
      if (wi.word) infoMap[wi.word.toLowerCase()] = wi;
    });
    setWordInfoMap(infoMap);
    setGameStats({
      totalWords: validTargets.length,
      correctFinds: 0,
      incorrectAttempts: 0,
      timeBonus: 0,
    });
    setGameState("playing");
    setFoundWords([]);
    setScore(0);
    setTimeLeft(MODE_CFG.time);

    // Clear any existing errors after successful initialization
    if (error) {
      setTimeout(() => setError(""), 3000);
    }
  };

  /**
   * Handles clicking on words in the passage
   * Manages scoring, feedback, and game progression
   * @param {string} word - The clicked word
   */
  const handleWordClick = (word) => {
    const cleanWord = word.toLowerCase().replace(/[.,!?;:"'()]/g, "");
    const matched = wordsToFind.find((t) => normForMatch(t) === normForMatch(cleanWord)) ?? null;

    if (matched) {
      if (!foundWords.includes(matched)) {
        setFoundWords([...foundWords, matched]);
        setScore(score + 10);
        setGameStats((prev) => ({
          ...prev,
          correctFinds: prev.correctFinds + 1,
        }));
        setFeedback({ message: `Found "${matched}"!`, type: "success" });

        if (foundWords.length + 1 === wordsToFind.length) {
          setTimeout(() => endGame(true), 1000);
        }
      } else {
        setFeedback({ message: `Already found "${matched}"`, type: "info" });
      }
    } else {
      setScore(Math.max(0, score - 2));
      setGameStats((prev) => ({
        ...prev,
        incorrectAttempts: prev.incorrectAttempts + 1,
      }));
      setFeedback({ message: `"${cleanWord}" not a target`, type: "error" });
    }

    setTimeout(() => setFeedback({ message: "", type: "" }), 2000);
  };

  /**
   * Ends the current game session
   * Calculates final score and awards coins
   */
  const endGame = (isExplicit = false) => {
    const rewards = calculateScoreAndCoins(gameStats, timeLeft);
    setGameStats((prev) => ({ ...prev, timeBonus: rewards.timeBonus }));
    setScore(rewards.finalScore);

    clearCachedGameData(selectedTier, selectedTopicCategory);
    if (isExplicit && !testMode) {
      addCoinsAndXPToUser(rewards.coinsEarned);
      saveNonRankedSession(rewards.finalScore);
    }

    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (userId && gameFilename && gameLevel != null) {
      apiFetch(`/api/mark-spot-played`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, filename: gameFilename, level: gameLevel }),
      }).catch(() => {});
    }

    setGameState("finished");
  };

  //====================================
  // SAVING GAME DATA
  //====================================

  // Prevent duplicate posts if endGame fires twice
  const hasPostedRef = React.useRef(false);
  const hasInitRef = React.useRef(false);

  // Completion animation refs
  const confettiCanvasRef = useRef(null);
  const confettiCleanupRef = useRef(null);

  // Fire confetti + fanfare + speech when game finishes
  useEffect(() => {
    if (gameState !== "finished") return;
    playVictoryFanfare();
    const timer = setTimeout(sayWellDone, 700);
    // canvas may need one frame to mount
    const rafId = requestAnimationFrame(() => {
      confettiCleanupRef.current = launchConfetti(confettiCanvasRef.current);
    });
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rafId);
      confettiCleanupRef.current?.();
      window.speechSynthesis?.cancel();
    };
  }, [gameState]);

  /** Save this finished Spot game to the DB (non-ranked rollup) */
  const saveNonRankedSession = async (finalScore) => {
    if (hasPostedRef.current) return; // guard
    hasPostedRef.current = true;

    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || Number.isNaN(userId)) return;

    try {
      const res = await apiFetch(`/api/sessions/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, mode: "spot", score: finalScore }),
      });

      if (!res.ok) {
        console.error("Session save failed:", await res.text());
      }
    } catch (e) {
      console.error("Error saving session:", e);
    }
  };

  // ====================================
  // UTILITY METHODS
  // ====================================

  /**
   * Formats seconds into MM:SS display format
   * @param {number} seconds - Time in seconds
   * @returns {string} - Formatted time string
   */
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ====================================
  // RENDER LOGIC
  // ====================================

  // Loading state rendering
  if (gameState === "loading") {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <BookOpen className={`spinner-icon ${loading ? "spinning" : ""}`} />
          <p>
            {loading
              ? "Generating your passage..."
              : "Preparing your challenge..."}
          </p>
          {error && (
            <div className="error-message">
              <AlertCircle />
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Game finished state rendering
  if (gameState === "finished") {
    const rewards = calculateScoreAndCoins(gameStats, 0); // Recalculate for display

    return (
      <>
      <canvas ref={confettiCanvasRef} className="confetti-canvas" />
      <div className="results-screen">
        <div className="results-content">
          <Trophy className="trophy-icon" />
          <h2>Game Complete!</h2>

          <div className="stats-grid">
            <StatItem
              icon={<Target />}
              label="Words Found"
              value={`${gameStats.correctFinds}/${gameStats.totalWords}`}
            />
            <StatItem
              icon={<Clock />}
              label="Time Bonus"
              value={`+${gameStats.timeBonus}`}
            />
            <StatItem icon={<Star />} label="Final Score" value={score} />
            <StatItem
              icon={<Coins />}
              label="Coins Earned"
              value={rewards.coinsEarned}
            />
          </div>

          <div className="action-buttons">
            <button
              className="primary-button"
              onClick={initializeGame}
              disabled={loading}
            >
              <RefreshCw className={loading ? "spinning" : ""} />
              New Game
            </button>
            <button
              className="secondary-button"
              onClick={() => window.history.back()}
            >
              <ArrowLeft /> Back to Modes
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  // Main game playing state rendering
  return (
    <div className="spot-word-game game-container">
      <GameHeader
        timeLeft={timeLeft}
        score={score}
        formatTime={formatTime}
        onBack={() => navigate("/games/comprehension")}
        testMode={testMode}
        onTestMode={() => setShowTestModeModal(true)}
      />

      {showTestModeModal && (
        <div className="test-mode-modal-overlay">
          <div className="test-mode-modal">
            <div className="test-mode-modal-icon"><Clock size={26} /></div>
            <h3>Enable Learning Mode?</h3>
            <p>The timer will stop and you'll have unlimited time to finish, but <strong>no coins or XP</strong> will be awarded. Your attempt will still be saved to your history.</p>
            <div className="test-mode-modal-actions">
              <button className="test-mode-confirm-btn" onClick={() => { setTestMode(true); setShowTestModeModal(false); }}>
                Confirm — Enable Learning Mode
              </button>
              <button className="test-mode-cancel-btn" onClick={() => setShowTestModeModal(false)}>
                Cancel — Keep Playing Normally
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback notifications */}
      {feedback.message && (
        <FeedbackMessage message={feedback.message} type={feedback.type} />
      )}

      {/* Error banner */}
      {error && (
        <div className="error-banner">
          <AlertCircle />
          {error}
        </div>
      )}

      {/* Main game content */}
      <div className="game-content">
        <PassageSection
          passage={passage}
          wordsToFind={wordsToFind}
          foundWords={foundWords}
          handleWordClick={handleWordClick}
          wordInfoMap={wordInfoMap}
        />

        <GameSidebar
          wordsToFind={wordsToFind}
          foundWords={foundWords}
          progress={(foundWords.length / wordsToFind.length) * 100}
          wordInfoMap={wordInfoMap}
        />
      </div>
    </div>
  );
};

// ====================================
// SUB-COMPONENTS
// ====================================

/**
 * Game header component displaying timer and score
 */
const GameHeader = ({ timeLeft, score, formatTime, onBack, testMode, onTestMode }) => (
  <header className="game-header">
    <div className="header-content">
      <h1>
        <Search className="search-icon" /> Spot the Word
      </h1>
    </div>
    <div className="header-right">
      <div className="header-stats">
        <div className={`timer ${!testMode && timeLeft < 15 ? "urgent" : ""}`}>
          <Clock /> {testMode ? "∞" : formatTime(timeLeft)}
        </div>
        <div className="score">
          <Star /> {score}
        </div>
        {testMode
          ? <span className="test-mode-badge"><Clock size={11} /> Learning Mode</span>
          : <button className="test-mode-btn" onClick={onTestMode}><Clock size={13} /> Learning Mode</button>
        }
      </div>
    </div>
    <button className="header-back-btn" onClick={onBack}>
      <ArrowLeft /> Back to Dashboard
    </button>
  </header>
);

/**
 * Main passage section where players click on words
 */
const PassageSection = ({ passage, wordsToFind, foundWords, handleWordClick, wordInfoMap }) => {
  const getWordStyle = (matched) =>
    foundWords.includes(matched) ? "found-word" : "clickable-word";

  return (
    <section className="passage-section">
      <div className="passage-header">Reading Passage</div>
      <div className="passage-text">
        {passage.split(/\s+/).map((word, index) => {
          const clean = word.toLowerCase().replace(/[.,!?;:"'()]/g, "");
          const matched = wordsToFind.find((t) => normForMatch(t) === normForMatch(clean)) ?? null;
          const isTarget = matched !== null;
          const info = matched ? wordInfoMap[matched] : null;

          if (isTarget && info) {
            return (
              <span key={index} className="word-tooltip-wrap">
                <span
                  className={getWordStyle(matched)}
                  onClick={() => handleWordClick(word)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleWordClick(word); } }}
                >
                  {word}
                </span>
                <span className="word-tooltip">
                  <span className="wt-word">{matched}</span>
                  <span className="wt-meaning">{info.meaning}</span>
                  {info.synonyms?.length > 0 && (
                    <span className="wt-row">
                      <span className="wt-tag syn">Synonyms</span>
                      {info.synonyms.join(", ")}
                    </span>
                  )}
                  {info.antonyms?.length > 0 && (
                    <span className="wt-row">
                      <span className="wt-tag ant">Antonyms</span>
                      {info.antonyms.join(", ")}
                    </span>
                  )}
                </span>
                {" "}
              </span>
            );
          }

          return (
            <span
              key={index}
              className={isTarget ? getWordStyle(matched) : "clickable-word"}
              onClick={() => handleWordClick(word)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleWordClick(word); } }}
            >
              {word}{" "}
            </span>
          );
        })}
      </div>
    </section>
  );
};

/**
 * Sidebar showing word list and progress tracking
 */
const GameSidebar = ({ wordsToFind, foundWords, progress, wordInfoMap }) => (
  <aside className="game-sidebar">
    <div className="word-list">
      <h3>Words to Find ({foundWords.length}/{wordsToFind.length})</h3>
      <div className="word-grid">
        {wordsToFind.map((word, index) => (
          <WordItem
            key={index}
            word={word}
            isFound={foundWords.includes(word)}
            info={wordInfoMap[word.toLowerCase()]}
          />
        ))}
      </div>
    </div>

    <div className="progress-section">
      <h3>Progress</h3>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <p>{Math.round(progress)}% Complete</p>
    </div>
  </aside>
);

/**
 * Individual word item component for the sidebar list
 */
const WordItem = ({ word, isFound, info }) => (
  <div className={`word-item ${isFound ? "found" : ""} ${info ? "has-info" : ""}`}>
    {isFound ? (
      <CheckCircle className="found-icon" size={16} />
    ) : (
      <XCircle className="missing-icon" size={16} />
    )}
    <span className="word-label">{word}</span>
    {info && (
      <span className="word-tooltip word-tooltip--sidebar">
        <span className="wt-word">{word}</span>
        <span className="wt-meaning">{info.meaning}</span>
        {info.synonyms?.length > 0 && (
          <span className="wt-row">
            <span className="wt-tag syn">Synonyms</span>
            {info.synonyms.join(", ")}
          </span>
        )}
        {info.antonyms?.length > 0 && (
          <span className="wt-row">
            <span className="wt-tag ant">Antonyms</span>
            {info.antonyms.join(", ")}
          </span>
        )}
      </span>
    )}
  </div>
);

/**
 * Statistics display item for the results screen
 */
const StatItem = ({ icon, label, value }) => (
  <div className="stat-item">
    <div className="stat-icon">{icon}</div>
    <div className="stat-info">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  </div>
);

/**
 * Feedback message component for user notifications
 */
const FeedbackMessage = ({ message, type }) => (
  <div className={`feedback-message ${type}`}>{message}</div>
);

export default SpotWordGame;
